import i18next from "i18next";
import moment from "moment";
import React from "react";
import {
  AddInvestigationInput,
  GeneralRadioWithInput,
  GeneralSelectWithInput,
  MedicalRichTextEditor,
} from "..";
import {
  DateInput,
  GeneralInput,
  GeneralSelectApi,
  UnitInput,
} from "../../../../components";
import { MedicalFields, MedicalKeys } from "../../../../enums/MedicalReportEnums";
import { IStepFields } from "../../../../interface/MedicalRecord";
import FollowUpAppointment from "../InputsTypesComponents/Appointment/FollowUpAppointment";
import MedicalGeneralSelect from "../InputsTypesComponents/MedicalGeneralSelect/MedicalGeneralSelect";
import Prescription from "../InputsTypesComponents/Prescription/Prescription";
import RepeatableSection from "../RepeatableSection/RepeatableSection";
import PregnancyInputs from "../PregnancyInputs/PregnancyInputs";

interface FieldRendererProps {
  field: IStepFields;
  disabled?: boolean;
}

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  disabled,
}) => {
  moment.locale(i18next.language);
  const getOptions = () => {
    return field?.InverseParentStepFields?.filter(
      (item) => item?.Type === MedicalFields.Option,
    )?.map((option) => {
      return {
        label: option?.Name,
        value: option?.Key ?? option?.Id,
        key: option?.Name,
        ...option,
      };
    });
  };

  const customStyleClassName =
    "w-full !h-[50px] !border !border-body-text !bg-white placeholder:!text-text-muted";

  const isLMP = field?.Key === MedicalKeys.LMP

  const fieldName =field?.Key ?? `Attributes.${String(field?.Id)}`;


  if (isLMP)
    return <PregnancyInputs field={field} />
  switch (field.Type) {
    case MedicalFields.Radio:
      return (
        <>
          <GeneralRadioWithInput
            name={fieldName}
            label={field?.Name}
            disabled={disabled}
            options={getOptions()}
            fieldConfig={field}
            isRequired={field?.IsRequired}
          />

        </>
      );

    case MedicalFields.Repeater:
      return (
        <RepeatableSection
          field={field}
          disabled={disabled}
        />
      );

    case MedicalFields.Select:
      return (
        <MedicalGeneralSelect
          className={customStyleClassName}
          name={fieldName}
          placeholder={field?.Name}
          selectOptions={getOptions()}
          isDisabled={disabled}
          field={field}
          isRequired={field?.IsRequired}
          optionsHight="10rem"
        />
      );
    case MedicalFields.MultiSelect:
      return (
        <MedicalGeneralSelect
          isMulti
          className={customStyleClassName}
          name={fieldName}
          placeholder={field?.Name}
          selectOptions={getOptions()}
          isDisabled={disabled}
          field={field}
          isRequired={field?.IsRequired}
          optionsHight="10rem"
        />
      );

    case MedicalFields.Unit:
      return (
        <UnitInput
          label={field?.Name}
          name={fieldName}
          type={field?.Unit as any}
          readOnly={disabled}
          customStyleClasses={customStyleClassName}
          isRequired={field?.IsRequired}
          isFloat={!field?.Key}
        />
      );

    case MedicalFields.Date:
      return (
        <DateInput
          readOnly={disabled}
          label={field?.Name}
          name={fieldName}
          className={`medical-date ${customStyleClassName}`}
        />
      );

    case MedicalFields.PastDate:
      return (
        <DateInput
          readOnly={disabled}
          label={field?.Name}
          dateConstraint="past"
          name={fieldName}
          className={`medical-date ${customStyleClassName}`}
        />
      );

    case MedicalFields.FutureDate:
      return (
        <DateInput
          readOnly={disabled}
          label={field?.Name}
          dateConstraint="future"
          name={fieldName}
          className={`medical-date ${customStyleClassName}`}
        />
      );

    case MedicalFields.CurrentDateTime:
      return (
        <DateInput
          readOnly
          value={moment().locale("en").format("DD-MM-YYYY HH:mm:ss")}
          label={field?.Name}
          name={fieldName}
        // customStyleClasses={`medical-date ${customStyleClassName} !text-primary-text`}
        />
      );

    case MedicalFields.Paragraph:
      return (
        <div className="flex flex-col gap-4">
          <p className="text-primary-textFont font-bold">{field?.Name}</p>
          {field?.InverseParentStepFields &&
            field.InverseParentStepFields.length > 0 && (
              <div className="flex flex-col gap-4">
                {field.InverseParentStepFields.map((childField) => (
                  <FieldRenderer
                    key={childField.Id}
                    field={childField}
                    disabled={disabled}
                  />
                ))}
              </div>
            )}
        </div>
      );
    case MedicalFields.TextArea:
      return (
        <GeneralInput
          type="textarea"
          label={field?.Name}
          name={fieldName}
          placeholder={field.Name}
          required={false}
          disabled={disabled}
          customStyleClasses={`${customStyleClassName} !min-h-32`}
        />
      );

    case MedicalFields.SelectApi:
      return (
        <GeneralSelectApi
          placeholder={field?.Name}
          name={fieldName}
          endpoint={field?.ApiUrl ?? ""}
          isClearable
          isDisabled={disabled}
          placeholderCustomStyle={{
            color: "#3F4869 !important",
          }}
          optionsHight="10rem"
        />
      );
    case MedicalFields.SelectWithInputs:
      return (
        <GeneralSelectWithInput
          placeholder={field?.Name}
          name={fieldName}
          endpoint={field?.ApiUrl ?? ""}
          placeholderCustomStyle={{
            color: "#3F4869 !important",
          }}
          isDisabled={disabled}
          isClearable
          fieldsConfig={field}
          optionsHight="10rem"
          isRequired={field?.IsRequired}
        />
      );

    case MedicalFields.MultiFile:
      return <AddInvestigationInput disabled={disabled} field={{ ...field }} />;

    case MedicalFields.TextEditor:
      return <MedicalRichTextEditor disabled={disabled} field={{ ...field }} />;
    case MedicalFields.Appointment:
      return <FollowUpAppointment name={fieldName} fieldsConfig={field} />;
    case MedicalFields.Prescription:
      return (
        <Prescription
          name={fieldName}
          fieldsConfig={field}
          disabled={disabled}
        />
      );



    default:
      return (
        <GeneralInput
          customStyleClasses={customStyleClassName}
          type="text"
          placeholder={field?.Name}
          label={field?.Name}
          name={fieldName}
          disabled={disabled}
          {...field}
        />
      );
  }
};
